import {
    userStorageUpdate,
    isIncompleteProfile,
    processCertificatesArray,
    setLocalItem,
    setSnackbar,
    getCache,
    setCache,
    removeCache,
    ROLES,
} from '@/helpers';

import {
    getMe,
    getMyCertificates,
    updateUser,
    deleteUser,
    deleteProfile,
    logoutUser,
    mergeUsers,
    setToken,
    giveConsentID06,
} from '../../Api/ApiUser';
import { updateRequest } from '@/System/Store/endpoints';

const initialState = () => ({
    hasFormError: false,
    user: {
        current_profile: null,
        current_profile_id: null,
        email: '',
        first_name: '',
        foreign_national_identity_number: null,
        incomplete_user: null,
        last_name: '',
        national_identity_number: null,
        notification_sms: false,
        notification_email: false,
        phone_number: '',
        profiles: [],
        addons: [],
        token: null,
    },
    bankID: null,
});

const state = initialState();

const mutations = {
    SET_BANK_ID(state, obj) {
        state.bankID = {
            orderRef: obj.orderRef,
            autoStartToken: obj.autoStartToken,
            status: null,
            hintCode: '',
        };
    },
    SET_TOKEN(state, token) {
        state.user.token = token;
        setLocalItem('token', token);
    },
    SET_HINT_AND_STATUS_CODE(state, obj) {
        state.bankID.status = obj.status;
        state.bankID.hintCode = obj.hintCode;
    },
    SET_USER(state, data) {
        let cache = getCache();

        if (!cache) {
            // The session vanished from storage (expired) while the request was in flight.
            // Leave the state alone; the session-expired handler resets it. Nulling it here
            // would make every getter reading state.user throw and blank the whole app.
            return;
        }

        // LocalStorage
        if (data) {
            if (!cache.token) {
                cache.token = state.user.token;
            }

            const primaryProfile = data.profiles.find((profile) => profile.is_primary);

            cache.id = data.id;
            cache.current_profile = data.current_profile;
            cache.current_profile_id = data.current_profile.id;
            cache.email = primaryProfile.email;
            cache.first_name = primaryProfile.first_name;
            cache.last_name = primaryProfile.last_name;
            cache.phone_number = primaryProfile.phone_number;
            cache.national_identity_number = data.national_identity_number;
            cache.foreign_national_identity_number = data.foreign_national_identity_number;
            cache.has_unverified_national_identity_number = data?.has_unverified_national_identity_number || false;

            cache.notification_email = data.notification_email;
            cache.notification_sms = Boolean(data.notification_sms && data.phone_number);
            cache.profiles = data.profiles;
            cache.addons = data.addons;

            // Set flag for no-user
            cache.incomplete_user = data.has_incomplete_profile;

            state.notification_email = data.notification_email;
            state.notification_sms = Boolean(data.notification_sms && data.phone_number);

            if (data?.onboarding) {
                cache.onboarding = data.onboarding;
            }
        }

        // State
        state.user = cache;
        setCache(cache);
    },
    SET_INCOMPLETE_USER(state, data) {
        state.user.incomplete_user = data;

        setLocalItem('incomplete_user', state.user.incomplete_user);
    },
    SET_UPDATED_USER(state, field) {
        const key = Object.keys(field)[0];
        state.user[key] = field[key];
    },
    PUSH_USER_PROFILE: (state, profile) => state.user.profiles.push(profile),
    CHANGE_CURRENT_PROFILE_ID: (state, current_profile_id) => (state.user.current_profile_id = current_profile_id),
    RESET_BANK_ID: (state) => (state.bankID = null),
    RESET_STATE: (state) => Object.assign(state, initialState()),
};

const actions = {
    async GET_ME({ commit, dispatch, rootGetters }) {
        return await getMe().then((response) => {
            if (response.status === 'Success') {
                commit('SET_USER', response.data);
                dispatch('Departments/SET_DEPARTMENTS', response.data.departments, { root: true });

                if (response.data.current_department_type) {
                    dispatch('UserPermissionStore/FETCH_USER_PERMISSIONS', response.data.current_profile.id, {
                        root: true,
                    });
                }

                if (response.data.current_department_type === 'employ') {
                    dispatch('AdminStore/FETCH_MY_ROLE', response.data.current_profile.id, { root: true });

                    if (rootGetters['Departments/GET_ACTIVE_PLAN']?.features.find((i) => i.name === 'employTeams')) {
                        dispatch('AdminStore/FETCH_MY_TEAMS', response.data.current_profile.id, { root: true });
                    } else {
                        dispatch('EmployeeStore/GET_EMPLOYEES', null, { root: true }).then(() =>
                            commit('AdminStore/SET_MY_COLLEAGUES', rootGetters['EmployeeStore/GET_EMPLOYEES'], {
                                root: true,
                            })
                        );
                    }
                }
            }

            if (isIncompleteProfile(response)) {
                commit('SET_INCOMPLETE_USER', {
                    incomplete: true,
                    email: response.data.email,
                });
            }

            return response.status === 'Success';
        });
    },
    async GET_MY_CERTIFICATES({ commit }) {
        commit('CertificateStore/SET_LOADING', true, { root: true });
        await getMyCertificates().then((response) => {
            if (response.status === 'Success') {
                commit('CertificateStore/SET_CERTIFICATES', processCertificatesArray(response.data, []), {
                    root: true,
                });
                commit('CertificateStore/SET_OUTGOING', null, { root: true });
                commit('CertificateStore/SET_EXPIRED', null, { root: true });
            }
            commit('CertificateStore/SET_LOADING', false, { root: true });
        });
    },
    async UPDATE_USER({ state, commit }) {
        let btnStatus = 'pending';

        await commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });

        return await updateUser({
            first_name: state.user.first_name,
            last_name: state.user.last_name,
            email: state.user.email,
            phone_number: state.user.phone_number,
            notification_email: state.user.notification_email,
            notification_sms: Boolean(state.user.notification_sms && state.user.phone_number),
        }).then((response) => {
            btnStatus = response.status;

            if (response.status === 'Success') {
                userStorageUpdate(response.data);
            } else {
                const snackbar = setSnackbar(response);
                commit('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
            }

            commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });

            return response.status === 'Success';
        });
    },

    async DELETE_USER({ dispatch }) {
        await deleteUser().then((response) => {
            if (response.status === 'Success') {
                dispatch('LOGOUT_USER');
            }
        });
    },

    async DELETE_PROFILE({ dispatch }, profile_id) {
        await deleteProfile(profile_id).then((response) => {
            if (response.status === 'Success') {
                dispatch('LOGOUT_USER');
            }
        });
    },

    CHANGE_PROFILE({ commit }, profile_id) {
        if (profile_id) {
            const profile = state.user.profiles.find((p) => p.id === profile_id);

            if (profile) {
                const cache = getCache();

                if (!cache) {
                    return;
                }

                cache.current_profile_id = profile_id;
                setCache(cache);

                commit('CHANGE_CURRENT_PROFILE_ID', profile);
            }
        }
    },

    async MERGE_USERS({ dispatch }, payload) {
        await mergeUsers(payload).then(async (response) => {
            if (response.status === 'Success') {
                if (response.data.token) {
                    const token = response.data.token.split('|').pop();

                    setToken(token);
                    localStorage.setItem('system_cache', JSON.stringify({ token: token }));

                    dispatch('GET_ME');
                }
            }

            if (response.status === 'Error') {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return false;
            }
        });
    },

    async LOGOUT_USER() {
        await logoutUser();

        if (removeCache()) {
            // Reload the browser, the router will take care of the redirection.
            location.reload();
        }
    },

    async CONSENT_ID06({ commit }) {
        await giveConsentID06().then((response) => {
            const snackbar = setSnackbar(response);
            commit('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
        });
    },

    async SET_ONBOARDING_STATUS({ commit }, payload) {
        return updateRequest('/users/me/onboarding', payload).then((response) => {
            if (response.status === 'Success') {
                const cache = getCache();
                cache.onboarding = response.data.onboarding;
                setCache(cache);
                commit('SET_UPDATED_USER', { onboarding: response.data.onboarding });
            } else {
                const snackbar = setSnackbar(response);
                commit('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
            }

            return response.status === 'Success';
        });
    },
};

const getters = {
    CAN_USER: (state, getters, rootState) => (what) => {
        const [domain, ability] = what.split('.');
        const { current_profile } = state.user;

        if (null === current_profile || null === ability) {
            return false;
        }
        const { system_role } = current_profile;
        let sure = false;
        switch (system_role.id) {
            case ROLES.SUPER_ADMIN:
                if ('superadmin' === domain) {
                    sure = true;
                }
                break;
            default:
                break;
        }

        if (rootState?.Departments?.currentDepartment) {
            const departmentRole = rootState.Departments.currentDepartment.user_role;
            switch (departmentRole.id) {
                case ROLES.CERTIFIER:
                    if ('certificate' === domain) {
                        // Unlimited power over certificates
                        sure = true;
                    }
                    break;
                case ROLES.ORGANIZATION_ADMIN:
                    if ('employ' === domain) {
                        // Unlimited power over employ
                        sure = true;
                    }
                    break;
                case ROLES.ORGANIZATION_TEAM_ADMIN:
                    if ('employ' === domain) {
                        // Unlimited power over employ
                        sure = true;
                    }
                    break;
                case ROLES.USER:
                    if ('certificate' === domain) {
                        if ('read' === ability) {
                            sure = true;
                        }
                    }
                    break;
                default:
                    break;

                case ROLES.ORGANIZATION_USER:
                    if ('employ' === domain) {
                        if ('read' === ability) {
                            sure = true;
                        }
                    }
            }
        }
        return sure;
    },
    GET_USER: (state) => state.user,
    GET_USER_PROFILES: (state) => state.user.profiles,
    GET_PRIMARY_USER_PROFILE: (state) => state.user.profiles.find((profile) => profile.is_primary),
    GET_CURRENT_USER_PROFILE: (state) => state.user.current_profile_id,
    GET_TOKEN: (state) => {
        const cache = getCache();

        if (state.user.token) {
            return state.user.token;
        }

        if (cache && cache.token) {
            return cache.token;
        }

        return null;
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
